import { supabase } from "../../config/supabaseClient";
import type { QueryClient } from "react-query";
import { getViewerStatus } from "../../Utils/HelperFunction/TimezoneDates";
import {
  createComponentSlug,
  generateOpaqueRouteToken,
} from "../../Utils/componentsTable";
import {
  getCreatorPageCreatedFromForInsert,
  getCreatorPageRecordForSourceComponent,
  isCreatorComponentPage,
  isCreatorPageSourceLandingComponent,
  normalizePageCreatedFrom,
  type PageCreatedFrom,
} from "../pageOrigins";
import { normalizeSoundValue } from "../../Utils/helpers/carouselHelper";
import { isProductCollectionType } from "../../Utils/productCollection";

export const CREATOR_PAGES_QUERY_KEY = "creator-pages";

const CREATOR_PAGES_CACHE_SCOPES = [
  "sidebar",
  "dashboard",
  "create-page-modal",
  "creators-component",
  "public",
] as const;

export function getCreatorPagesQueryKeys(userId?: string) {
  if (!userId) return [];
  return CREATOR_PAGES_CACHE_SCOPES.map(
    (scope) => [CREATOR_PAGES_QUERY_KEY, userId, scope] as const
  );
}

export function removeCreatorPageFromCache(
  queryClient: QueryClient,
  pageId: string,
  userId?: string
) {
  if (!pageId) return;

  const updatePages = (cachedPages: unknown) => {
    if (!Array.isArray(cachedPages)) return cachedPages;
    const nextPages = cachedPages.filter((page: any) => page?.id !== pageId);
    return nextPages.length === cachedPages.length ? cachedPages : nextPages;
  };

  const keysToUpdate = userId
    ? [
        [CREATOR_PAGES_QUERY_KEY, userId],
        ...getCreatorPagesQueryKeys(userId),
      ]
    : [];

  keysToUpdate.forEach((queryKey) => {
    queryClient.setQueryData(queryKey, updatePages);
  });

  queryClient
    .getQueryCache()
    .findAll(CREATOR_PAGES_QUERY_KEY)
    .forEach((query) => {
      queryClient.setQueryData(query.queryKey, updatePages);
    });
}

export async function invalidateCreatorPagesQueries(
  queryClient: QueryClient,
  userId?: string
) {
  const invalidations = [queryClient.invalidateQueries(CREATOR_PAGES_QUERY_KEY)];

  if (userId) {
    invalidations.push(
      queryClient.invalidateQueries([CREATOR_PAGES_QUERY_KEY, userId]),
      ...getCreatorPagesQueryKeys(userId).map((queryKey) =>
        queryClient.invalidateQueries(queryKey)
      )
    );
  }

  await Promise.all(invalidations);
}

function normalizeSlug(value: string) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Same normalization used when saving creator page slugs (configure + create flows). */
export function normalizeCreatorPageSlug(value: string) {
  return normalizeSlug(value);
}

export function isCreatorPageSlugTaken(
  slug: string,
  pages: Array<{ id?: string; slug?: string }>,
  excludePageId?: string
) {
  const normalizedSlug = normalizeSlug(slug);
  if (!normalizedSlug) return false;

  return pages.some((page) => {
    if (excludePageId && page?.id === excludePageId) return false;
    return normalizeSlug(page?.slug || "") === normalizedSlug;
  });
}

function generateCreatorPageSlugToken(length = 10) {
  return generateOpaqueRouteToken(length);
}

/** Manual slug in "Create New Page" modal: letters, digits, and hyphens. */
export function sanitizeCreatorPageManualSlug(value: string) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-{2,}/g, "-");
}

export function isValidCreatorPageManualSlug(value: string) {
  const s = String(value || "").trim().toLowerCase();
  // Allow hyphenated routes (e.g. look-book); no leading/trailing hyphen.
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(s);
}

function normalizePageName(value: string) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

export function normalizeCreatorPageName(value: string) {
  return normalizePageName(value);
}

const LINK_IN_BIO_NAME_CHECK_STATUSES = new Set([
  "active",
  "archive",
  "draft",
  "default",
]);

export const LINK_IN_BIO_CAROUSEL_NAME_TAKEN_MESSAGE =
  "A carousel with this name already exists on your link-in-bio (active, archive, or draft). Choose a different page name.";

export type LinkInBioCarouselNameRow = {
  id?: string;
  name?: string;
  route_name?: string;
  unique_id?: string;
  status?: string;
  status_expiry?: string;
};

function isLinkInBioCarouselCountedForNameCheck(row: LinkInBioCarouselNameRow) {
  const status = String(row?.status || "").toLowerCase().trim();
  const expiry = String(row?.status_expiry || "").toLowerCase().trim();
  if (status === "deleted" || expiry === "deleted") return false;
  if (
    LINK_IN_BIO_NAME_CHECK_STATUSES.has(status) ||
    LINK_IN_BIO_NAME_CHECK_STATUSES.has(expiry)
  ) {
    return true;
  }
  // Rows with no status still live in Active / Archive / Draft tabs.
  return !status && !expiry;
}

function isExcludedLinkedLinkInBioCarousel(
  row: LinkInBioCarouselNameRow,
  excludePage?: { name?: string; slug?: string; created_from?: string | null }
) {
  if (!excludePage || !isCreatorComponentPage(excludePage.created_from)) {
    return false;
  }

  const pageSlug = normalizeIdentityToken(excludePage.slug);
  const route = normalizeIdentityToken(row?.route_name);
  const uniqueId = normalizeIdentityToken(row?.unique_id);
  if (pageSlug && (pageSlug === route || pageSlug === uniqueId)) {
    return true;
  }

  return (
    normalizePageName(row?.name || "") ===
    normalizePageName(excludePage.name || "")
  );
}

export function isLinkInBioCarouselNameTaken(
  name: string,
  carousels: LinkInBioCarouselNameRow[] = [],
  excludePage?: { name?: string; slug?: string; created_from?: string | null }
) {
  const normalizedName = normalizePageName(name);
  if (!normalizedName) return false;
  if (
    excludePage?.name &&
    normalizedName === normalizePageName(excludePage.name)
  ) {
    return false;
  }

  return (Array.isArray(carousels) ? carousels : []).some((row) => {
    if (!isLinkInBioCarouselCountedForNameCheck(row)) return false;
    if (normalizePageName(row?.name || "") !== normalizedName) return false;
    return !isExcludedLinkedLinkInBioCarousel(row, excludePage);
  });
}

export async function listLinkInBioCarouselsForNameCheck(
  userId: string
): Promise<LinkInBioCarouselNameRow[]> {
  const id = String(userId || "").trim();
  if (!id) return [];

  const { data, error } = await supabase
    .from("stayuptodate_components")
    .select("id,name,route_name,unique_id,status,status_expiry,type")
    .eq("user_id", id)
    .neq("status", "deleted");

  if (error) throw error;
  return (Array.isArray(data) ? data : []).filter(
    (row) => !isProductCollectionType(row)
  );
}

async function ensureLinkInBioCarouselNameAvailable({
  userId,
  name,
  excludePage,
}: {
  userId: string;
  name: string;
  excludePage?: { name?: string; slug?: string; created_from?: string | null };
}) {
  const carousels = await listLinkInBioCarouselsForNameCheck(userId);
  if (isLinkInBioCarouselNameTaken(name, carousels, excludePage)) {
    throw new Error(LINK_IN_BIO_CAROUSEL_NAME_TAKEN_MESSAGE);
  }
}

const DEDICATED_EMBED_PAGE_NORMALIZED_NAME = "embed page";

export const DEDICATED_EMBED_PAGE_EXISTS_MESSAGE =
  "You already have an Embed Page from the Embed flow.";

/** Names that would duplicate the dedicated Embed flow page inside multipage (`creator_pages`). */
export function isEmbedLikeCreatorPageName(name: string) {
  const normalized = normalizePageName(name);
  if (!normalized) return false;
  if (
    normalized === DEDICATED_EMBED_PAGE_NORMALIZED_NAME ||
    normalized === "embed"
  ) {
    return true;
  }
  // e.g. "Embed Page Copy", "Embed Page 2" from duplicate flows
  return normalized.startsWith(`${DEDICATED_EMBED_PAGE_NORMALIZED_NAME} `);
}

/**
 * True when the creator already has the dedicated Embed Page (Embed flow), not a multipage clone.
 * Checks `user.is_embed_search` and non-deleted rows in `embed_components`.
 */
export async function userHasDedicatedEmbedPage(userId: string): Promise<boolean> {
  const id = String(userId || "").trim();
  if (!id) return false;

  const { data: userRow, error: userError } = await supabase
    .from("user")
    .select("is_embed_search")
    .eq("user_id", id)
    .maybeSingle();

  if (userError) throw userError;
  if (userRow?.is_embed_search) return true;

  return userHasEmbedComponentRows(id);
}

/** True when the creator has a real Embed Page (non-deleted embed_components). */
export async function userHasEmbedComponentRows(userId: string): Promise<boolean> {
  const id = String(userId || "").trim();
  if (!id) return false;

  const { count, error } = await supabase
    .from("embed_components")
    .select("id", { count: "exact", head: true })
    .eq("user_id", id)
    .neq("status", "deleted");

  if (error) throw error;
  return (count ?? 0) > 0;
}

async function assertNoDuplicateDedicatedEmbedCreatorPage(
  userId: string,
  name: string
) {
  // Intentionally no-op: Creator Pages creation/configuration should only enforce
  // standard uniqueness (name/slug) and must not block on Embed flow presence.
  // The Embed flow itself should own preventing multiple embed pages in `embed_components`.
  void userId;
  void name;
}

async function ensureUniqueCreatorPageIdentity({
  userId,
  name,
  slug,
  excludePageId,
}: {
  userId: string;
  name: string;
  slug: string;
  excludePageId?: string;
}) {
  const { data, error } = await supabase
    .from("creator_pages")
    .select("id,name,slug")
    .eq("user_id", userId)
    .neq("status", "deleted");

  if (error) {
    throw error;
  }

  const existingPages = Array.isArray(data) ? data : [];
  const conflictingPage = existingPages.find((page: any) => {
    if (excludePageId && page?.id === excludePageId) {
      return false;
    }

    return (
      normalizePageName(page?.name || "") === normalizePageName(name) ||
      normalizeSlug(page?.slug || "") === normalizeSlug(slug)
    );
  });

  if (!conflictingPage) {
    return;
  }

  const hasSameName =
    normalizePageName(conflictingPage?.name || "") === normalizePageName(name);
  const hasSameSlug =
    normalizeSlug(conflictingPage?.slug || "") === normalizeSlug(slug);

  if (hasSameName) {
    throw new Error("A page with this name already exists.");
  }

  if (hasSameSlug) {
    throw new Error("A page with this slug already exists.");
  }
}

async function ensureCreatorPageSlugUniqueGlobally({
  slug,
  excludePageId,
}: {
  slug: string;
  excludePageId?: string;
}) {
  const normalizedSlug = normalizeSlug(slug);
  if (!normalizedSlug) {
    throw new Error("Page slug is required.");
  }

  const { data, error } = await supabase
    .from("creator_pages")
    .select("id,slug")
    .neq("status", "deleted")
    .eq("slug", normalizedSlug)
    .limit(1);

  if (error) {
    throw error;
  }

  const row = Array.isArray(data) ? data[0] : null;
  if (!row) return;
  if (excludePageId && row.id === excludePageId) return;

  throw new Error("A page with this slug already exists.");
}

async function buildUniqueCreatorPageName({
  userId,
  desiredName,
}: {
  userId: string;
  desiredName: string;
}) {
  const normalizedDesired = String(desiredName || "").trim();
  const base = normalizedDesired || "Untitled Page";

  const { data, error } = await supabase
    .from("creator_pages")
    .select("name")
    .eq("user_id", userId)
    .neq("status", "deleted")
    .limit(500);

  if (error) throw error;

  const existingNames = new Set(
    (Array.isArray(data) ? data : [])
      .map((row: any) => normalizePageName(row?.name || ""))
      .filter(Boolean)
  );

  if (!existingNames.has(normalizePageName(base))) {
    return base;
  }

  for (let n = 2; n <= 200; n += 1) {
    const candidate = `${base} ${n}`;
    if (!existingNames.has(normalizePageName(candidate))) {
      return candidate;
    }
  }

  return `${base} ${generateCreatorPageSlugToken(6)}`;
}

function buildDefaultSettings() {
  return {
    showProfilePicture: true,
    showNameAndDesc: true,
    showSocialLinks: true,
    showSearchbar: true,
    showCreatorCTA: true,
    maxWidthPercent: 60,
  };
}

function hasExplicitActiveStatusOverride(overrides: Record<string, any> = {}) {
  const status = String(overrides?.status || "")
    .toLowerCase()
    .trim();
  const statusExpiry = String(overrides?.status_expiry || "")
    .toLowerCase()
    .trim();
  return status === "active" && statusExpiry === "active";
}

function buildSafePageComponentPayload(
  component: any,
  pageId: string,
  overrides: Record<string, any> = {}
) {
  const source = component || {};
  const routeName =
    overrides.route_name ||
    source?.route_name ||
    source?.unique_id ||
    createComponentSlug(source?.name || "component");
  const uniqueId =
    overrides.unique_id ||
    source?.unique_id ||
    source?.route_name ||
    createComponentSlug(source?.name || "component");

  const mergedStatusSource = { ...source, ...overrides };
  const rawStatus = overrides.status || source?.status;
  const rawStatusExpiry = overrides.status_expiry || source?.status_expiry;
  let status = rawStatus || "active";
  let status_expiry = rawStatusExpiry || "active";
  const forceActive = hasExplicitActiveStatusOverride(overrides);

  if (forceActive) {
    status = "active";
    status_expiry = "active";
  } else if (
    isScheduledStatusExpiry(rawStatusExpiry) ||
    isScheduledStatusExpiry(rawStatus) ||
    componentHasScheduledDates(mergedStatusSource)
  ) {
    const resolved = resolvePageComponentScheduleStatus(mergedStatusSource);
    status = resolved;
    status_expiry = resolved;
  } else {
    const manual = String(status_expiry || status || "active")
      .toLowerCase()
      .trim();
    if (["draft", "active", "archive", "deleted"].includes(manual)) {
      status = manual;
      status_expiry = manual;
    } else {
      status = "active";
      status_expiry = "active";
    }
  }

  return {
    page_id: pageId,
    user_id: source?.user_id,
    username: source?.username,
    name: source?.name || "Untitled Component",
    details: source?.details || "",
    index: typeof source?.index === "number" ? source.index : 0,
    type: source?.type || "",
    post_ids: source?.post_ids || "",
    display_name: source?.display_name || source?.name || "",
    cta_placeholder: source?.cta_placeholder || "Check it out!",
    query: source?.query || "",
    content_order: source?.content_order || "DESC",
    content_shape: source?.content_shape || "rect",
    content_sound: normalizeSoundValue(source?.content_sound, true),
    shoppable_content_filter: source?.shoppable_content_filter || "both",
    custom_order: source?.custom_order || "",
    pinned_posts: source?.pinned_posts || null,
    shoppable_status_filter: source?.shoppable_status_filter || "all",
    description: source?.description || "",
    status,
    status_expiry,
    route_name: routeName,
    unique_id: uniqueId,
    ...(forceActive
      ? {
          start_date: null,
          archive_date: null,
        }
      : {}),
  };
}

async function cleanupFailedDuplicatePage(pageId?: string | null) {
  if (!pageId) return;

  try {
    await supabase
      .from("creator_pages")
      .update({ status: "deleted", expanded_component_id: null })
      .eq("id", pageId);
  } catch {
    // Preserve the original duplicate/copy error for the user.
  }
}

function normalizeIdentityToken(value: any) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function buildIdentityTokens(values: any[] = []) {
  return new Set(
    values
      .map((value) => normalizeIdentityToken(value))
      .filter(Boolean)
  );
}

function doesComponentMatchByRouteIdentity(
  component: any,
  routeIdentityTokens: Set<string>
) {
  if (!routeIdentityTokens.size) return false;

  return (
    routeIdentityTokens.has(normalizeIdentityToken(component?.route_name)) ||
    routeIdentityTokens.has(normalizeIdentityToken(component?.unique_id))
  );
}

function doesComponentMatchByNameIdentity(
  component: any,
  nameIdentityTokens: Set<string>,
  sourceComponent?: any
) {
  if (!nameIdentityTokens.size) return false;

  const componentName = normalizeIdentityToken(component?.name);
  const sourceType = normalizeIdentityToken(sourceComponent?.type);
  const componentType = normalizeIdentityToken(component?.type);

  return (
    nameIdentityTokens.has(componentName) &&
    (!sourceType || !componentType || sourceType === componentType)
  );
}

function dedupeRowsById(rows: any[] = []) {
  return rows.filter(
    (row: any, index: number, allRows: any[]) =>
      row?.id && allRows.findIndex((item: any) => item?.id === row.id) === index
  );
}

type CreatorPageSyncContext = {
  id?: string;
  name?: string;
  slug?: string;
  created_from?: string | null;
  expanded_component_id?: string | null;
};

async function fetchCreatorPageSyncContext(
  pageId?: string | null
): Promise<CreatorPageSyncContext | null> {
  if (!pageId) return null;

  const { data, error } = await supabase
    .from("creator_pages")
    .select("id,name,slug,created_from,expanded_component_id")
    .eq("id", pageId)
    .neq("status", "deleted")
    .maybeSingle();

  if (error) throw error;
  return data || null;
}

function allowsLegacyCreatorComponentNameSync(page?: CreatorPageSyncContext | null) {
  return isCreatorComponentPage(page?.created_from);
}

function filterComponentsByRouteIdentity(
  components: any[] = [],
  routeIdentityTokens: Set<string>
) {
  return components.filter((component: any) =>
    doesComponentMatchByRouteIdentity(component, routeIdentityTokens)
  );
}

function filterComponentsByRouteOrLegacyName(
  components: any[] = [],
  options: {
    routeIdentityTokens: Set<string>;
    nameIdentityTokens: Set<string>;
    referenceComponent?: any;
    allowLegacyNameMatch: boolean;
  }
) {
  const routeMatches = filterComponentsByRouteIdentity(
    components,
    options.routeIdentityTokens
  );
  if (routeMatches.length) {
    return routeMatches;
  }

  if (!options.allowLegacyNameMatch || !options.nameIdentityTokens.size) {
    return [];
  }

  return components.filter((component: any) =>
    doesComponentMatchByNameIdentity(
      component,
      options.nameIdentityTokens,
      options.referenceComponent
    )
  );
}

function resolveLinkedPageComponentsForPageSync(
  pageComponents: any[] = [],
  options: {
    routeIdentityTokens: Set<string>;
    nameIdentityTokens: Set<string>;
  },
  page?: CreatorPageSyncContext | null
) {
  const allowLegacyNameMatch = allowsLegacyCreatorComponentNameSync(page);

  let linkedPageComponents = page?.expanded_component_id
    ? pageComponents.filter(
        (component: any) => component?.id === page.expanded_component_id
      )
    : [];

  if (!linkedPageComponents.length) {
    linkedPageComponents = filterComponentsByRouteOrLegacyName(pageComponents, {
      routeIdentityTokens: options.routeIdentityTokens,
      nameIdentityTokens: options.nameIdentityTokens,
      allowLegacyNameMatch,
    });
  }

  if (
    !linkedPageComponents.length &&
    allowLegacyNameMatch &&
    pageComponents.length === 1
  ) {
    linkedPageComponents = pageComponents;
  }

  return dedupeRowsById(linkedPageComponents);
}

export async function listCreatorPages(userId: string) {
  const { data, error } = await supabase
    .from("creator_pages")
    .select("*")
    .eq("user_id", userId)
    .neq("status", "deleted")
    .order("created_at", { ascending: true });

  if (error) {
    throw error;
  }

  return Array.isArray(data) ? data : [];
}

export async function createCreatorPage({
  userId,
  name,
  slug,
  pageId,
  settings,
  createdFrom,
}: {
  userId: string;
  name: string;
  slug?: string;
  pageId?: string;
  settings?: Record<string, any>;
  createdFrom?: PageCreatedFrom | null;
}) {
  const normalizedName = String(name || "").trim() || "Untitled Page";

  const userProvidedSlug = Boolean(String(slug || "").trim());
  const initialSlug = normalizeSlug(slug || "") || "";
  let normalizedSlug = initialSlug || createComponentSlug(normalizedName);

  // If the user already has the dedicated Embed flow page, prevent a creator_page
  // from taking the exact same identity (name/slug).
  if (await userHasDedicatedEmbedPage(userId)) {
    if (normalizePageName(normalizedName) === "embed page") {
      throw new Error('A page named "Embed Page" already exists from the Embed flow.');
    }
    if (userProvidedSlug && normalizeSlug(normalizedSlug) === "embed-page") {
      throw new Error('A page with slug "embed-page" already exists from the Embed flow.');
    }
    if (userProvidedSlug && normalizeSlug(normalizedSlug) === "collections") {
      throw new Error('The slug "collections" is reserved.');
    }
  }

  if (!userProvidedSlug) {
    // Prefer an opaque, DB-unique route token (not derived from carousel/page name).
    normalizedSlug = generateCreatorPageSlugToken();
  }

  const assertSlugAvailable = async () => {
    await ensureCreatorPageSlugUniqueGlobally({ slug: normalizedSlug });
    await ensureUniqueCreatorPageIdentity({
      userId,
      name: normalizedName,
      slug: normalizedSlug,
    });
  };

  if (userProvidedSlug) {
    // Manual slugs must be unique — same enforcement as configure/update (no auto-retry).
    await assertSlugAvailable();
  } else {
    // Auto-generated slugs can retry on rare collisions.
    for (let attempt = 0; attempt < 5; attempt += 1) {
      try {
        await assertSlugAvailable();
        break;
      } catch (error: any) {
        const message = String(error?.message || "");
        const isSlugConflict = message.toLowerCase().includes("slug");
        if (!isSlugConflict || attempt === 4) {
          throw error;
        }
        normalizedSlug = generateCreatorPageSlugToken();
      }
    }
  }

  const payload = {
    ...(pageId ? { id: pageId } : {}),
    user_id: userId,
    name: normalizedName,
    slug: normalizedSlug,
    status: "draft",
    settings: {
      ...buildDefaultSettings(),
      ...(settings || {}),
    },
    created_from: getCreatorPageCreatedFromForInsert(createdFrom),
    expanded_component_id:  null,
  };

  const { data, error } = await supabase
    .from("creator_pages")
    .insert([payload])
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

export async function updateCreatorPage(
  pageId: string,
  updates: Record<string, any>
) {
  const normalizedUpdates = { ...updates };

  if (updates?.name || updates?.slug) {
    const { data: existingPage, error: existingPageError } = await supabase
      .from("creator_pages")
      .select("id,user_id,name,slug,created_from")
      .eq("id", pageId)
      .single();

    if (existingPageError) throw existingPageError;

    const nextName = String(updates?.name ?? existingPage?.name ?? "").trim();
    const nextSlug = normalizeSlug(updates?.slug ?? existingPage?.slug ?? nextName);

    if (await userHasDedicatedEmbedPage(existingPage.user_id)) {
      if (updates?.name && normalizePageName(nextName) === "embed page") {
        throw new Error('A page named "Embed Page" already exists from the Embed flow.');
      }
      if (updates?.slug && normalizeSlug(nextSlug) === "embed-page") {
        throw new Error('A page with slug "embed-page" already exists from the Embed flow.');
      }
      if (updates?.slug && normalizeSlug(nextSlug) === "collections") {
        throw new Error('The slug "collections" is reserved.');
      }
    }

    if (
      updates?.name &&
      normalizePageName(nextName) !== normalizePageName(existingPage?.name || "")
    ) {
      await ensureLinkInBioCarouselNameAvailable({
        userId: existingPage.user_id,
        name: nextName,
        excludePage: {
          name: existingPage?.name,
          slug: existingPage?.slug,
          created_from: existingPage?.created_from,
        },
      });
    }

    await ensureUniqueCreatorPageIdentity({
      userId: existingPage.user_id,
      name: nextName || existingPage?.name || "Untitled Page",
      slug: nextSlug,
      excludePageId: pageId,
    });

    if (updates?.name) {
      normalizedUpdates.name = nextName;
    }
    if (updates?.slug) {
      normalizedUpdates.slug = nextSlug;
    }
  }

  const { data, error } = await supabase
    .from("creator_pages")
    .update(normalizedUpdates)
    .eq("id", pageId)
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

export async function updateExpandedCreatorPageComponent(
  pageId: string,
  componentId: string | null
) {
  const { data, error } = await supabase
    .from("creator_pages")
    .update({ expanded_component_id: componentId })
    .eq("id", pageId)
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

export async function syncCreatorPageMetadataFromSourceComponent({
  userId,
  previousComponent,
  nextComponent,
}: {
  userId: string;
  previousComponent?: any;
  nextComponent?: any;
}) {
  if (!userId) return;

  const routeIdentityTokens = buildIdentityTokens([
    previousComponent?.route_name,
    previousComponent?.unique_id,
    nextComponent?.route_name,
    nextComponent?.unique_id,
  ]);
  const nameIdentityTokens = buildIdentityTokens([
    previousComponent?.name,
    nextComponent?.name,
  ]);

  if (!routeIdentityTokens.size && !nameIdentityTokens.size) {
    return;
  }

  const [
    { data: pagesData, error: pagesError },
    { data: pageComponentsData, error: pageComponentsError },
  ] = await Promise.all([
    supabase
      .from("creator_pages")
      .select("id,name,slug")
      .eq("user_id", userId)
      .neq("status", "deleted"),
    supabase
      .from("page_components")
      .select("id,page_id,name,type,route_name,unique_id")
      .eq("user_id", userId)
      .neq("status", "deleted"),
  ]);

  if (pagesError) throw pagesError;
  if (pageComponentsError) throw pageComponentsError;

  const pageComponents = Array.isArray(pageComponentsData)
    ? pageComponentsData
    : [];

  const pages = Array.isArray(pagesData) ? pagesData : [];
  const creatorComponentPageIds = new Set(
    pages
      .filter((page: any) => isCreatorComponentPage(page?.created_from))
      .map((page: any) => page?.id)
      .filter(Boolean)
  );

  let matchedPageComponents = filterComponentsByRouteIdentity(
    pageComponents,
    routeIdentityTokens
  );

  if (!matchedPageComponents.length && creatorComponentPageIds.size) {
    matchedPageComponents = pageComponents.filter(
      (component: any) =>
        creatorComponentPageIds.has(component?.page_id) &&
        doesComponentMatchByNameIdentity(
          component,
          nameIdentityTokens,
          previousComponent || nextComponent
        )
    );
  }

  const matchedPageIds = new Set(
    matchedPageComponents
      .map((component: any) => component?.page_id)
      .filter(Boolean)
  );

  const matchedPages = pages.filter((page: any) => {
    if (matchedPageIds.has(page?.id)) {
      return true;
    }

    return (
      isCreatorComponentPage(page?.created_from) &&
      routeIdentityTokens.size &&
      routeIdentityTokens.has(normalizeIdentityToken(page?.slug))
    );
  });

  const pageIdsToUpdate = matchedPages
    .map((page: any) => page?.id)
    .filter(Boolean);

  const previousName = String(previousComponent?.name || "").trim();
  const nextName = String(nextComponent?.name || "").trim();
  const previousRoute = normalizeIdentityToken(previousComponent?.route_name);
  const nextRoute = String(nextComponent?.route_name || "").trim();
  const previousUniqueId = normalizeIdentityToken(previousComponent?.unique_id);
  const nextUniqueId = String(nextComponent?.unique_id || "").trim();

  const pageUpdates: Record<string, any> = {};
  const pageComponentUpdates: Record<string, any> = {};

  if (nextName && nextName !== previousName) {
    pageUpdates.name = nextName;
    pageComponentUpdates.name = nextName;
  }

  if (normalizeIdentityToken(nextRoute) !== previousRoute && nextRoute) {
    pageUpdates.slug =
      normalizeSlug(nextRoute || nextUniqueId || nextName) ||
      createComponentSlug(nextName || "page");
    pageComponentUpdates.route_name = nextRoute;
  } else if (
    normalizeIdentityToken(nextUniqueId) !== previousUniqueId &&
    nextUniqueId
  ) {
    pageUpdates.slug =
      normalizeSlug(nextUniqueId || nextName) ||
      createComponentSlug(nextName || "page");
  }

  if (
    normalizeIdentityToken(nextUniqueId) !== previousUniqueId &&
    nextUniqueId
  ) {
    pageComponentUpdates.unique_id = nextUniqueId;
  }

  if (pageIdsToUpdate.length && Object.keys(pageUpdates).length) {
    await Promise.all(
      pageIdsToUpdate.map((pageId) => updateCreatorPage(pageId, pageUpdates))
    );
  }

  const pageComponentIdsToUpdate = matchedPageComponents
    .map((component: any) => component?.id)
    .filter(Boolean);

  if (
    pageComponentIdsToUpdate.length &&
    Object.keys(pageComponentUpdates).length
  ) {
    const { error } = await supabase
      .from("page_components")
      .update(pageComponentUpdates)
      .in("id", pageComponentIdsToUpdate)
      .eq("user_id", userId)
      .neq("status", "deleted");

    if (error) throw error;
  }
}

export async function syncSourceComponentMetadataFromCreatorPage({
  userId,
  pageId,
  previousPage,
  nextPage,
}: {
  userId: string;
  pageId: string;
  previousPage?: any;
  nextPage?: any;
}) {
  if (!userId || !pageId) return;

  const previousPageName = String(previousPage?.name || "").trim();
  const nextPageName = String(nextPage?.name || "").trim();
  const previousPageSlug = normalizeSlug(previousPage?.slug || previousPageName);
  const nextPageSlug = normalizeSlug(nextPage?.slug || nextPageName);

  const [
    { data: pageComponentsData, error: pageComponentsError },
    { data: sourceComponentsData, error: sourceComponentsError },
  ] = await Promise.all([
    supabase
      .from("page_components")
      .select("id,page_id,name,type,route_name,unique_id")
      .eq("user_id", userId)
      .eq("page_id", pageId)
      .neq("status", "deleted"),
    supabase
      .from("stayuptodate_components")
      .select("id,name,type,route_name,unique_id")
      .eq("user_id", userId)
      .neq("status", "deleted"),
  ]);

  if (pageComponentsError) throw pageComponentsError;
  if (sourceComponentsError) throw sourceComponentsError;

  const pageComponents = Array.isArray(pageComponentsData) ? pageComponentsData : [];
  const sourceComponents = Array.isArray(sourceComponentsData)
    ? sourceComponentsData
    : [];

  if (!pageComponents.length || !sourceComponents.length) {
    return;
  }

  const routeIdentityTokens = buildIdentityTokens([
    previousPageSlug,
    nextPageSlug,
  ]);
  const nameIdentityTokens = buildIdentityTokens([
    previousPageName,
    nextPageName,
  ]);

  const dedupedLinkedPageComponents = resolveLinkedPageComponentsForPageSync(
    pageComponents,
    {
      routeIdentityTokens,
      nameIdentityTokens,
    },
    previousPage
  );

  if (!dedupedLinkedPageComponents.length) {
    return;
  }

  const allowLegacyNameMatch = allowsLegacyCreatorComponentNameSync(previousPage);
  const matchedSourceComponents = dedupeRowsById(
    dedupedLinkedPageComponents.flatMap((pageComponent: any) => {
      const componentRouteTokens = buildIdentityTokens([
        pageComponent?.route_name,
        pageComponent?.unique_id,
        previousPageSlug,
      ]);
      const componentNameTokens = buildIdentityTokens([
        pageComponent?.name,
        previousPageName,
      ]);

      return filterComponentsByRouteOrLegacyName(sourceComponents, {
        routeIdentityTokens: componentRouteTokens,
        nameIdentityTokens: componentNameTokens,
        referenceComponent: pageComponent,
        allowLegacyNameMatch,
      });
    })
  );

  const componentNameChanged = nextPageName && nextPageName !== previousPageName;
  const componentRouteChanged = nextPageSlug && nextPageSlug !== previousPageSlug;

  const pageComponentUpdates: Record<string, any> = {};
  const sourceComponentUpdates: Record<string, any> = {};

  if (componentNameChanged) {
    pageComponentUpdates.name = nextPageName;
    sourceComponentUpdates.name = nextPageName;
  }

  if (componentRouteChanged) {
    pageComponentUpdates.route_name = nextPageSlug;
    sourceComponentUpdates.route_name = nextPageSlug;
  }

  const linkedPageComponentIds = dedupedLinkedPageComponents
    .map((component: any) => component?.id)
    .filter(Boolean);
  const matchedSourceIds = matchedSourceComponents
    .map((component: any) => component?.id)
    .filter(Boolean);

  if (linkedPageComponentIds.length && Object.keys(pageComponentUpdates).length) {
    const { error } = await supabase
      .from("page_components")
      .update(pageComponentUpdates)
      .in("id", linkedPageComponentIds)
      .eq("user_id", userId)
      .neq("status", "deleted");

    if (error) throw error;
  }

  if (matchedSourceIds.length && Object.keys(sourceComponentUpdates).length) {
    const { error } = await supabase
      .from("stayuptodate_components")
      .update(sourceComponentUpdates)
      .in("id", matchedSourceIds)
      .eq("user_id", userId)
      .neq("status", "deleted");

    if (error) throw error;
  }
}

export async function syncSourceComponentMetadataFromPageComponent({
  userId,
  previousPageComponent,
  nextPageComponent,
}: {
  userId: string;
  previousPageComponent?: any;
  nextPageComponent?: any;
}) {
  if (!userId) return;

  const pageId =
    nextPageComponent?.page_id || previousPageComponent?.page_id || null;
  const page = await fetchCreatorPageSyncContext(pageId);
  const pageComponentForLinkage = previousPageComponent || nextPageComponent;

  const previousComponentName = String(previousPageComponent?.name || "").trim();
  const nextComponentName = String(nextPageComponent?.name || "").trim();
  const previousComponentRoute = normalizeIdentityToken(
    previousPageComponent?.route_name
  );
  const nextComponentRoute = String(nextPageComponent?.route_name || "").trim();
  const previousComponentUniqueId = normalizeIdentityToken(
    previousPageComponent?.unique_id
  );
  const nextComponentUniqueId = String(nextPageComponent?.unique_id || "").trim();

  const routeIdentityTokens = buildIdentityTokens([
    previousPageComponent?.route_name,
    previousPageComponent?.unique_id,
    nextPageComponent?.route_name,
    nextPageComponent?.unique_id,
  ]);
  const nameIdentityTokens = buildIdentityTokens([
    previousComponentName,
    nextComponentName,
  ]);

  const { data: sourceComponentsData, error: sourceComponentsError } =
    await supabase
      .from("stayuptodate_components")
      .select("id,name,type,route_name,unique_id")
      .eq("user_id", userId)
      .neq("status", "deleted");

  if (sourceComponentsError) throw sourceComponentsError;

  const sourceComponents = Array.isArray(sourceComponentsData)
    ? sourceComponentsData
    : [];

  if (!sourceComponents.length) {
    return;
  }

  let matchedSourceComponents = filterComponentsByRouteIdentity(
    sourceComponents,
    routeIdentityTokens
  );

  const allowLegacyNameMatch =
    allowsLegacyCreatorComponentNameSync(page) &&
    isCreatorPageSourceLandingComponent({
      pageCreatedFrom: page?.created_from,
      creatorPageSlug: page?.slug,
      component: pageComponentForLinkage,
    });

  if (!matchedSourceComponents.length && allowLegacyNameMatch) {
    matchedSourceComponents = sourceComponents.filter((component: any) =>
      doesComponentMatchByNameIdentity(
        component,
        nameIdentityTokens,
        pageComponentForLinkage
      )
    );
  }

  if (!matchedSourceComponents.length) {
    return;
  }

  const matchedSourceIds = dedupeRowsById(matchedSourceComponents)
    .map((component: any) => component?.id)
    .filter(Boolean);

  if (!matchedSourceIds.length) {
    return;
  }

  const sourceComponentUpdates: Record<string, any> = {};

  if (nextComponentName && nextComponentName !== previousComponentName) {
    sourceComponentUpdates.name = nextComponentName;
  }

  if (
    normalizeIdentityToken(nextComponentRoute) !== previousComponentRoute &&
    nextComponentRoute
  ) {
    sourceComponentUpdates.route_name = nextComponentRoute;
  }

  if (
    normalizeIdentityToken(nextComponentUniqueId) !== previousComponentUniqueId &&
    nextComponentUniqueId
  ) {
    sourceComponentUpdates.unique_id = nextComponentUniqueId;
  }

  if (!Object.keys(sourceComponentUpdates).length) {
    return;
  }

  const { error } = await supabase
    .from("stayuptodate_components")
    .update(sourceComponentUpdates)
    .in("id", matchedSourceIds)
    .eq("user_id", userId)
    .neq("status", "deleted");

  if (error) throw error;
}

/** Carousel content/settings synced between link-in-bio and linked creator pages only. */
export const CREATOR_COMPONENT_SETTINGS_SYNC_FIELDS = [
  "details",
  "original_banner_image",
  "image_crop",
  "post_ids",
  "query",
  "removed_postids",
  "content_order",
  "content_shape",
  "content_sound",
  "shoppable_content_filter",
  "custom_order",
  "pinned_posts",
  "shoppable_status_filter",
  "cta_placeholder",
  "description",
  "status",
  "status_expiry",
  "start_date",
  "archive_date",
  "owner_timezone",
] as const;

/** Schedule visibility on link-in-bio must not drive page_components status. */
const PAGE_COMPONENT_SCHEDULE_EXCLUDED_SYNC_FIELDS = new Set([
  "status",
  "status_expiry",
  "start_date",
  "archive_date",
]);

function normalizeSettingsSyncValue(value: any) {
  if (value === undefined || value === null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
  }
  return value;
}

function areSettingsSyncValuesEqual(previousValue: any, nextValue: any) {
  return (
    normalizeSettingsSyncValue(previousValue) ===
    normalizeSettingsSyncValue(nextValue)
  );
}

const MANUAL_STATUS_EXPIRY_VALUES = new Set(["active", "draft", "archive"]);
const PAGE_COMPONENT_ALLOWED_STATUSES = new Set([
  "active",
  "draft",
  "archive",
  "deleted",
]);

type ComponentSettingsSyncTarget =
  | "page_components"
  | "stayuptodate_components";

function isScheduledStatusExpiry(value: any) {
  return String(value || "").toLowerCase().trim() === "default";
}

function isManualStatusExpiry(value: any) {
  return MANUAL_STATUS_EXPIRY_VALUES.has(String(value || "").toLowerCase().trim());
}

function componentHasScheduledDates(component?: any) {
  return Boolean(
    normalizeSettingsSyncValue(component?.start_date) ||
      normalizeSettingsSyncValue(component?.archive_date)
  );
}

/** page_components only allows draft|active|archive|deleted — never "default". */
export function resolvePageComponentScheduleStatus(component?: any) {
  const computed = getViewerStatus({
    ...component,
    status_expiry: "active",
  });
  const normalized = String(computed || "active").toLowerCase().trim();
  if (PAGE_COMPONENT_ALLOWED_STATUSES.has(normalized)) {
    return normalized;
  }
  return "active";
}

function enforceStatusExpirySyncConsistency(
  updates: Record<string, any>,
  previousComponent?: any,
  nextComponent?: any,
  target: ComponentSettingsSyncTarget = "stayuptodate_components"
) {
  const result = { ...updates };
  const mergedNext = { ...previousComponent, ...nextComponent, ...result };

  const touchesManualStatus =
    ("status_expiry" in result && isManualStatusExpiry(result.status_expiry)) ||
    ("status" in result && isManualStatusExpiry(result.status));

  if (touchesManualStatus) {
    result.start_date = null;
    result.archive_date = null;
    const manualValue =
      "status_expiry" in result && isManualStatusExpiry(result.status_expiry)
        ? result.status_expiry
        : result.status;
    result.status = manualValue;
    result.status_expiry = manualValue;
  }

  const touchesSchedule =
    "start_date" in result ||
    "archive_date" in result ||
    ("status_expiry" in result && isScheduledStatusExpiry(result.status_expiry)) ||
    ("status" in result && isScheduledStatusExpiry(result.status));

  if (
    touchesSchedule &&
    (componentHasScheduledDates(mergedNext) ||
      isScheduledStatusExpiry(result.status_expiry) ||
      isScheduledStatusExpiry(result.status))
  ) {
    if (target === "page_components") {
      const resolved = resolvePageComponentScheduleStatus(mergedNext);
      result.status = resolved;
      result.status_expiry = resolved;
    } else {
      result.status = "default";
      result.status_expiry = "default";
    }
  }

  if (target === "page_components") {
    if (
      isScheduledStatusExpiry(result.status_expiry) ||
      isScheduledStatusExpiry(result.status) ||
      isScheduledStatusExpiry(mergedNext.status_expiry) ||
      isScheduledStatusExpiry(mergedNext.status)
    ) {
      const resolved = resolvePageComponentScheduleStatus(mergedNext);
      result.status = resolved;
      result.status_expiry = resolved;
    }
  }

  return result;
}

export function pickCreatorComponentSettingsForSync(component?: any) {
  const snapshot: Record<string, any> = {};
  for (const field of CREATOR_COMPONENT_SETTINGS_SYNC_FIELDS) {
    if (component && field in component) {
      snapshot[field] = component[field];
    }
  }
  return snapshot;
}

export function buildCreatorComponentSettingsSyncUpdates(
  previousComponent?: any,
  nextComponent?: any,
  target: ComponentSettingsSyncTarget = "stayuptodate_components"
) {
  const updates: Record<string, any> = {};
  const syncFields =
    target === "page_components"
      ? CREATOR_COMPONENT_SETTINGS_SYNC_FIELDS.filter(
          (field) => !PAGE_COMPONENT_SCHEDULE_EXCLUDED_SYNC_FIELDS.has(field)
        )
      : CREATOR_COMPONENT_SETTINGS_SYNC_FIELDS;
  for (const field of syncFields) {
    if (!nextComponent || !(field in nextComponent)) continue;
    const nextValue = nextComponent[field];
    const previousValue = previousComponent?.[field];
    if (!areSettingsSyncValuesEqual(previousValue, nextValue)) {
      updates[field] = nextValue;
    }
  }
  return enforceStatusExpirySyncConsistency(
    updates,
    previousComponent,
    nextComponent,
    target
  );
}

/** Link-in-bio carousel settings -> matching page_components on creator_component pages. */
export async function syncLinkedCreatorComponentSettingsFromSource({
  userId,
  previousComponent,
  nextComponent,
}: {
  userId: string;
  previousComponent?: any;
  nextComponent?: any;
}) {
  if (!userId) return;

  const settingsUpdates = buildCreatorComponentSettingsSyncUpdates(
    previousComponent,
    nextComponent,
    "page_components"
  );
  if (!Object.keys(settingsUpdates).length) {
    return;
  }

  const routeIdentityTokens = buildIdentityTokens([
    previousComponent?.route_name,
    previousComponent?.unique_id,
    nextComponent?.route_name,
    nextComponent?.unique_id,
  ]);
  const nameIdentityTokens = buildIdentityTokens([
    previousComponent?.name,
    nextComponent?.name,
  ]);

  if (!routeIdentityTokens.size && !nameIdentityTokens.size) {
    return;
  }

  const [
    { data: pagesData, error: pagesError },
    { data: pageComponentsData, error: pageComponentsError },
  ] = await Promise.all([
    supabase
      .from("creator_pages")
      .select("id,created_from")
      .eq("user_id", userId)
      .neq("status", "deleted"),
    supabase
      .from("page_components")
      .select("id,page_id,name,type,route_name,unique_id")
      .eq("user_id", userId)
      .neq("status", "deleted"),
  ]);

  if (pagesError) throw pagesError;
  if (pageComponentsError) throw pageComponentsError;

  const pages = Array.isArray(pagesData) ? pagesData : [];
  const pageComponents = Array.isArray(pageComponentsData)
    ? pageComponentsData
    : [];

  const creatorComponentPageIds = new Set(
    pages
      .filter((page: any) => isCreatorComponentPage(page?.created_from))
      .map((page: any) => page?.id)
      .filter(Boolean)
  );

  if (!creatorComponentPageIds.size) {
    return;
  }

  let matchedPageComponents = filterComponentsByRouteIdentity(
    pageComponents.filter((component: any) =>
      creatorComponentPageIds.has(component?.page_id)
    ),
    routeIdentityTokens
  );

  if (!matchedPageComponents.length) {
    matchedPageComponents = pageComponents.filter(
      (component: any) =>
        creatorComponentPageIds.has(component?.page_id) &&
        doesComponentMatchByNameIdentity(
          component,
          nameIdentityTokens,
          previousComponent || nextComponent
        )
    );
  }

  const pageComponentIdsToUpdate = dedupeRowsById(matchedPageComponents)
    .map((component: any) => component?.id)
    .filter(Boolean);

  if (!pageComponentIdsToUpdate.length) {
    return;
  }

  const { error } = await supabase
    .from("page_components")
    .update(settingsUpdates)
    .in("id", pageComponentIdsToUpdate)
    .eq("user_id", userId)
    .neq("status", "deleted");

  if (error) throw error;
}

/** Page carousel settings -> matching stayuptodate_components (creator_component pages only). */
export async function syncLinkedCreatorComponentSettingsFromPage({
  userId,
  previousPageComponent,
  nextPageComponent,
}: {
  userId: string;
  previousPageComponent?: any;
  nextPageComponent?: any;
}) {
  if (!userId) return;

  const settingsUpdates = buildCreatorComponentSettingsSyncUpdates(
    previousPageComponent,
    nextPageComponent,
    "stayuptodate_components"
  );
  if (!Object.keys(settingsUpdates).length) {
    return;
  }

  const pageId =
    nextPageComponent?.page_id || previousPageComponent?.page_id || null;
  const page = await fetchCreatorPageSyncContext(pageId);

  if (!allowsLegacyCreatorComponentNameSync(page)) {
    return;
  }

  const routeIdentityTokens = buildIdentityTokens([
    previousPageComponent?.route_name,
    previousPageComponent?.unique_id,
    nextPageComponent?.route_name,
    nextPageComponent?.unique_id,
  ]);
  const nameIdentityTokens = buildIdentityTokens([
    previousPageComponent?.name,
    nextPageComponent?.name,
  ]);

  const { data: sourceComponentsData, error: sourceComponentsError } =
    await supabase
      .from("stayuptodate_components")
      .select("id,name,type,route_name,unique_id")
      .eq("user_id", userId)
      .neq("status", "deleted");

  if (sourceComponentsError) throw sourceComponentsError;

  const sourceComponents = Array.isArray(sourceComponentsData)
    ? sourceComponentsData
    : [];

  if (!sourceComponents.length) {
    return;
  }

  let matchedSourceComponents = filterComponentsByRouteIdentity(
    sourceComponents,
    routeIdentityTokens
  );

  const pageComponentForLinkage = previousPageComponent || nextPageComponent;
  const allowLegacyNameMatch =
    allowsLegacyCreatorComponentNameSync(page) &&
    isCreatorPageSourceLandingComponent({
      pageCreatedFrom: page?.created_from,
      creatorPageSlug: page?.slug,
      component: pageComponentForLinkage,
    });

  if (!matchedSourceComponents.length && allowLegacyNameMatch) {
    matchedSourceComponents = sourceComponents.filter((component: any) =>
      doesComponentMatchByNameIdentity(
        component,
        nameIdentityTokens,
        pageComponentForLinkage
      )
    );
  }

  const matchedSourceIds = dedupeRowsById(matchedSourceComponents)
    .map((component: any) => component?.id)
    .filter(Boolean);

  if (!matchedSourceIds.length) {
    return;
  }

  const { error } = await supabase
    .from("stayuptodate_components")
    .update(settingsUpdates)
    .in("id", matchedSourceIds)
    .eq("user_id", userId)
    .neq("status", "deleted");

  if (error) throw error;
}

export async function deleteCreatorPage(pageId: string) {
  const { error } = await supabase
    .from("creator_pages")
    .update({ status: "deleted", expanded_component_id: null })
    .eq("id", pageId);

  if (error) throw error;
  return pageId;
}

/** Resolve a creator page that was created from a link-in-bio carousel. */
export function resolveLinkedCreatorComponentPage({
  pages = [],
  pageComponents = [],
  sourceComponent,
}: {
  pages?: any[];
  pageComponents?: any[];
  sourceComponent?: any;
}) {
  if (!sourceComponent) return null;

  const linkedPage = getCreatorPageRecordForSourceComponent({
    pages,
    pageComponents,
    sourceComponent,
  });

  if (!linkedPage?.id || !isCreatorComponentPage(linkedPage?.created_from)) {
    return null;
  }

  return linkedPage;
}

export async function findLinkedCreatorPageForSourceComponent({
  userId,
  sourceComponent,
}: {
  userId: string;
  sourceComponent?: any;
}) {
  if (!userId || !sourceComponent) return null;

  const [
    { data: pagesData, error: pagesError },
    { data: pageComponentsData, error: pageComponentsError },
  ] = await Promise.all([
    supabase
      .from("creator_pages")
      .select("id,name,slug,created_from,expanded_component_id")
      .eq("user_id", userId)
      .neq("status", "deleted"),
    supabase
      .from("page_components")
      .select("id,page_id,name,type,route_name,unique_id")
      .eq("user_id", userId)
      .neq("status", "deleted"),
  ]);

  if (pagesError) throw pagesError;
  if (pageComponentsError) throw pageComponentsError;

  return resolveLinkedCreatorComponentPage({
    pages: Array.isArray(pagesData) ? pagesData : [],
    pageComponents: Array.isArray(pageComponentsData) ? pageComponentsData : [],
    sourceComponent,
  });
}

/** Soft-delete a creator page that was created from a link-in-bio carousel. */
export async function deleteLinkedCreatorPageForSourceComponent({
  userId,
  sourceComponent,
}: {
  userId: string;
  sourceComponent?: any;
}) {
  const linkedPage = await findLinkedCreatorPageForSourceComponent({
    userId,
    sourceComponent,
  });

  if (!linkedPage?.id) return null;

  await deleteCreatorPage(linkedPage.id);
  return linkedPage.id;
}

export async function duplicateCreatorPage(page: any) {
  const userId = String(page?.user_id || "");
  if (!userId) {
    throw new Error("Unable to duplicate page. User not found.");
  }

  const baseName = String(page?.name || "Page").trim() || "Page";
  const desiredCopyName = `${baseName} Copy`;
  const resolvedCopyName = await buildUniqueCreatorPageName({
    userId,
    desiredName: desiredCopyName,
  });

  let duplicateSlug = generateCreatorPageSlugToken();
  for (let attempt = 0; attempt < 5; attempt += 1) {
    try {
      await ensureCreatorPageSlugUniqueGlobally({ slug: duplicateSlug });
      break;
    } catch (error: any) {
      const message = String(error?.message || "");
      const isSlugConflict = message.toLowerCase().includes("slug");
      if (!isSlugConflict || attempt === 4) {
        throw error;
      }
      duplicateSlug = generateCreatorPageSlugToken();
    }
  }

  const { data: sourceComponents, error: sourceError } = await supabase
    .from("page_components")
    .select("*")
    .eq("page_id", page.id)
    .neq("status", "deleted")
    .order("index", { ascending: false });

  if (sourceError) throw sourceError;

  const { data: copiedPage, error: pageError } = await supabase
    .from("creator_pages")
    .insert([
      {
        user_id: userId,
        name: resolvedCopyName,
        slug: duplicateSlug,
        status: "draft",
        settings: page.settings || buildDefaultSettings(),
        created_from: normalizePageCreatedFrom(page?.created_from),
        expanded_component_id: null,
      },
    ])
    .select("*")
    .single();

  if (pageError) throw pageError;

  if (Array.isArray(sourceComponents) && sourceComponents.length > 0) {
    const duplicatedComponents = sourceComponents.map((component: any) => {
      const { id, created_at, updated_at, status, status_expiry, ...rest } =
        component;
      const normalizedStatus = resolvePageComponentScheduleStatus(component);
      const resolvedStatus =
        String(status_expiry || status || "").toLowerCase().trim() === "default"
          ? normalizedStatus
          : ["draft", "active", "archive", "deleted"].includes(
                String(status_expiry || status || "")
                  .toLowerCase()
                  .trim()
              )
            ? String(status_expiry || status || "")
                .toLowerCase()
                .trim()
            : normalizedStatus;
      return {
        ...rest,
        page_id: copiedPage.id,
        status: resolvedStatus,
        status_expiry: resolvedStatus,
        route_name: component?.route_name
          ? createComponentSlug(component.route_name)
          : createComponentSlug(component?.name || "component"),
        unique_id: createComponentSlug(component?.unique_id || component?.name || "component"),
      };
    });

    const { error: copyError } = await supabase
      .from("page_components")
      .insert(duplicatedComponents);

    if (copyError) {
      await cleanupFailedDuplicatePage(copiedPage.id);
      throw copyError;
    }
  }

  return copiedPage;
}

export async function duplicateEmbedPageToCreatorPage(userId: string) {
  if (!userId) {
    throw new Error("Unable to duplicate embed page. User not found.");
  }

  const resolvedCopyName = await buildUniqueCreatorPageName({
    userId,
    desiredName: "Embed Page Copy",
  });
  let duplicateSlug = generateCreatorPageSlugToken();
  for (let attempt = 0; attempt < 5; attempt += 1) {
    try {
      await ensureCreatorPageSlugUniqueGlobally({ slug: duplicateSlug });
      break;
    } catch (error: any) {
      const message = String(error?.message || "");
      const isSlugConflict = message.toLowerCase().includes("slug");
      if (!isSlugConflict || attempt === 4) {
        throw error;
      }
      duplicateSlug = generateCreatorPageSlugToken();
    }
  }

  const { data: userRecord, error: userError } = await supabase
    .from("user")
    .select("embed_configure")
    .eq("user_id", userId)
    .single();

  if (userError) throw userError;

  const embedConfig = userRecord?.embed_configure || {};
  const seededSettings = {
    ...buildDefaultSettings(),
    showProfilePicture: embedConfig.showProfilePicture ?? true,
    showSocialLinks: embedConfig.showSocialLinks ?? true,
    showNameAndDesc: embedConfig.showNameAndDesc ?? false,
    showSearchbar: embedConfig.showSearchBar ?? true,
    // Embed flow historically stored this toggle as `showPoweredBySearchBubble`.
    // Prefer the canonical name if present, otherwise fall back for legacy rows.
    showCreatorCTA:
      embedConfig.showCreatorCTA ?? embedConfig.showPoweredBySearchBubble ?? true,
  };

  const { data: sourceComponents, error: sourceError } = await supabase
    .from("embed_components")
    .select("*")
    .eq("user_id", userId)
    .neq("status", "deleted")
    .order("index", { ascending: false });

  if (sourceError) throw sourceError;

  const { data: createdPage, error: pageError } = await supabase
    .from("creator_pages")
    .insert([
      {
        user_id: userId,
        name: resolvedCopyName,
        slug: duplicateSlug,
        status: "draft",
        settings: seededSettings,
        created_from: getCreatorPageCreatedFromForInsert(),
        expanded_component_id: null,
      },
    ])
    .select("*")
    .single();

  if (pageError) throw pageError;

  if (Array.isArray(sourceComponents) && sourceComponents.length > 0) {
    const duplicatedComponents = sourceComponents.map((component: any) =>
      buildSafePageComponentPayload(component, createdPage.id, {
        status: "active",
        status_expiry: "active",
      })
    );

    const { error: copyError } = await supabase
      .from("page_components")
      .insert(duplicatedComponents);

    if (copyError) {
      await cleanupFailedDuplicatePage(createdPage.id);
      throw copyError;
    }
  }

  return createdPage;
}

export async function disableEmbedPage(userId: string) {
  if (!userId) {
    throw new Error("Unable to disable embed page: missing user id.");
  }

  // Match single-component delete (StayUpToDateDatabase.deleteComponent): soft-delete embed rows.
  const { error: embedComponentsError } = await supabase
    .from("embed_components")
    .update({ status: "deleted", status_expiry: "deleted" })
    .eq("user_id", userId)
    .neq("status", "deleted");

  if (embedComponentsError) throw embedComponentsError;

  const { data, error } = await supabase
    .from("user")
    .update({ is_embed_search: false })
    .eq("user_id", userId)
    .select("user_id");

  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Unable to disable embed page: user row not found.");
  }

  return userId;
}

export async function createPageComponent(pageId: string, component: any) {
  const {
    id,
    created_at,
    updated_at,
    page_id,
    start_date: _startDate,
    archive_date: _archiveDate,
    status: _status,
    status_expiry: _statusExpiry,
    ...componentPayload
  } = component || {};

  const routeName =
    componentPayload?.route_name ||
    componentPayload?.unique_id ||
    createComponentSlug(componentPayload?.name || "component");
  const uniqueId =
    componentPayload?.unique_id ||
    componentPayload?.route_name ||
    createComponentSlug(componentPayload?.name || "component");
  const forceActiveOverrides = {
    status: "active",
    status_expiry: "active",
    start_date: null,
    archive_date: null,
    route_name: routeName,
    unique_id: uniqueId,
  };

  const fullPayload = {
    ...componentPayload,
    page_id: pageId,
    ...forceActiveOverrides,
  };

  const { data: fullInsertData, error: fullInsertError } = await supabase
    .from("page_components")
    .insert([fullPayload])
    .select("*")
    .single();

  if (!fullInsertError) {
    return fullInsertData;
  }

  // Some environments may have a narrower `page_components` schema than
  // `stayuptodate_components`. Retry with a safe minimal payload so page creation
  // still succeeds immediately after selecting an existing carousel.
  const minimalPayload = buildSafePageComponentPayload(
    componentPayload,
    pageId,
    forceActiveOverrides
  );

  const { data: minimalInsertData, error: minimalInsertError } = await supabase
    .from("page_components")
    .insert([minimalPayload])
    .select("*")
    .single();

  if (minimalInsertError) {
    throw minimalInsertError;
  }

  return minimalInsertData;
}